import { Button, Card } from '@starter/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { authClient } from '../lib/auth.ts';
import { internalPath } from '../lib/redirect.ts';

const MESSAGES = {
  sent: {
    title: 'Confirm your email',
    body: 'We sent you a link. Open it and the account is ready — the link is good for an hour.',
  },
  unverified: {
    title: 'This address is not confirmed yet',
    body: 'Sign-in needs a confirmed address. Send yourself a fresh link and open it.',
  },
  done: {
    title: 'Email confirmed',
    body: 'That is the last step — the account is yours.',
  },
} as const;

/**
 * Three moments in one screen, because they differ by one sentence and a
 * button: right after sign-up, after a sign-in that was refused, and after the
 * link itself was opened.
 */
function VerifyEmail() {
  const { state, email, redirectTo, error } = Route.useSearch();
  const [resent, setResent] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const message = MESSAGES[state];
  const canResend = state !== 'done' || Boolean(error);

  async function resend() {
    if (!email) return;
    setPending(true);
    setFailure(null);

    const { error: sendError } = await authClient.sendVerificationEmail({
      email,
      callbackURL: `/verify-email?state=done&redirectTo=${encodeURIComponent(redirectTo)}`,
    });

    setPending(false);
    if (sendError) {
      setFailure(sendError.message ?? 'Could not send the link');
      return;
    }
    setResent(true);
  }

  return (
    <div className="mx-auto max-w-sm">
      <Card title={error ? 'That link did not work' : message.title}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-muted" data-testid="verify-email-message">
            {error
              ? 'It may have expired or already been used. Ask for a new one below.'
              : message.body}
          </p>

          {email ? (
            <p className="text-sm font-medium text-ink" data-testid="verify-email-address">
              {email}
            </p>
          ) : null}

          {state === 'done' && !error ? (
            <Button render={<Link to={redirectTo} />} data-testid="verify-email-continue">
              Continue
            </Button>
          ) : null}

          {canResend && email ? (
            <Button
              variant="secondary"
              disabled={pending || resent}
              data-testid="verify-email-resend"
              onClick={resend}
            >
              {resent ? 'Link sent' : pending ? 'Sending…' : 'Send a new link'}
            </Button>
          ) : null}

          {failure ? (
            <p className="text-sm text-danger-text" role="alert" data-testid="verify-email-error">
              {failure}
            </p>
          ) : null}

          <Link to="/login" search={{}} className="text-sm text-ink-muted underline">
            Back to sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/verify-email')({
  validateSearch: z.object({
    state: z.enum(['sent', 'unverified', 'done']).default('sent'),
    email: z.email().optional(),
    redirectTo: z
      .string()
      .optional()
      .transform((value) => internalPath(value)),
    /** Better Auth appends this to the callback when the link is stale or used. */
    error: z.string().optional(),
  }),
  component: VerifyEmail,
});
